import { Link } from 'react-router';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { cn } from '@/lib/utils';
import { formatRelativeTime, formatDuration } from '@/lib/format';
import { JobStatusBadge } from './JobStatusBadge';
import { LivePollingIndicator } from './LivePollingIndicator';
import type { JobDetailDTO } from '@/types/db';

/**
 * `<JobDetailHeader>` — UI-SPEC § 3.6.1 detail-page header band.
 *
 * Layout:
 * - md+: 2-col grid `[1fr auto]` — main content on the left, status badge
 *   right-justified.
 * - <md: stacked.
 *
 * Slots:
 * - Address h1
 * - Meta line (job_name · started · duration)
 * - LivePollingIndicator (visible while polling, fades on terminal status)
 * - "View Property →" link when `status === 'completed'`
 * - JobStatusBadge (size=md, right column / bottom on mobile)
 *
 * The route (Plan 07-03) computes `isPolling` from `useJobStatus()` and
 * passes the job_name / started_at / duration from the cached `useJobs()`
 * payload (since `/api/jobs/:id/status` doesn't carry those v1.0-shape
 * fields).
 */
export interface JobDetailHeaderProps {
  job: JobDetailDTO;
  jobName: string | null;
  startedAt: string | null;
  durationSec: number | null;
  isPolling: boolean;
  lastUpdated?: Date | null;
}

export function JobDetailHeader({
  job,
  jobName,
  startedAt,
  durationSec,
  isPolling,
  lastUpdated,
}: JobDetailHeaderProps) {
  const metaParts: string[] = [];
  metaParts.push(jobName ? jobName : `Job #${job.job_id}`);
  metaParts.push(`started ${formatRelativeTime(startedAt)}`);
  metaParts.push(`duration ${formatDuration(durationSec)}`);

  const finalStatus =
    job.status === 'completed' || job.status === 'failed'
      ? job.status
      : undefined;

  return (
    <GlassPanel
      variant="strong"
      as="header"
      className={cn(
        'p-4',
        'flex flex-col gap-3',
        'md:grid md:grid-cols-[1fr_auto] md:gap-6 md:items-center md:p-6',
      )}
    >
      <div className="flex flex-col gap-2 min-w-0">
        <h1 className="text-xl font-semibold text-foam-50 break-words">
          {job.address}
        </h1>
        <p className="text-sm text-foam-50/70">{metaParts.join(' · ')}</p>
        <LivePollingIndicator
          isPolling={isPolling}
          lastUpdated={lastUpdated}
          finalStatus={finalStatus}
        />
        {job.status === 'completed' && job.property_id ? (
          <Link
            to={`/properties/${job.property_id}`}
            className={cn(
              'text-tide-400 hover:text-tide-400/80 underline underline-offset-2',
              'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded',
              'text-sm font-semibold self-start',
            )}
          >
            View Property →
          </Link>
        ) : null}
      </div>
      <div className="md:justify-self-end">
        <JobStatusBadge status={job.status} size="md" />
      </div>
    </GlassPanel>
  );
}
